import React, { useEffect, useRef } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Card,
  Button,
  CircularProgress,
  styled,
  Divider
} from '@mui/material';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';
import { format } from 'date-fns';
import ScheduleTwoToneIcon from '@mui/icons-material/ScheduleTwoTone';

const DividerWrapper = styled(Divider)(
  ({ theme }) => `
    .MuiDivider-wrapper {
      border-radius: ${theme.general.borderRadiusSm};
      text-transform: none;
      background: ${theme.palette.background.default};
      font-size: ${theme.typography.pxToRem(13)};
      color: ${theme.colors.alpha.black[50]};
    }
`
);

const CardWrapperPrimary = styled(Card)(
  ({ theme }) => `
    background: ${theme.colors.primary.main};
    color: ${theme.palette.primary.contrastText};
    padding: ${theme.spacing(2)};
    border-radius: ${theme.general.borderRadiusXl};
    border-top-right-radius: ${theme.general.borderRadius};
    max-width: 460px;
    display: inline-flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `
);

const CardWrapperSecondary = styled(Card)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[10]};
    color: ${theme.colors.alpha.black[100]};
    padding: ${theme.spacing(2)};
    border-radius: ${theme.general.borderRadiusXl};
    border-top-left-radius: ${theme.general.borderRadius};
    max-width: 460px;
    display: inline-flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `
);

const ChatContainer = styled(Box)(
  ({ theme }) => `
    height: 100%;
    overflow-y: auto;
    padding: ${theme.spacing(3)};
  `
);

const MessageImage = styled('img')(
  ({ theme }) => `
    display: block;
    max-width: min(320px, 70vw);
    max-height: 360px;
    border-radius: ${theme.general.borderRadius};
    object-fit: cover;
  `
);

const MessageVideo = styled('video')(
  ({ theme }) => `
    display: block;
    max-width: min(320px, 70vw);
    max-height: 360px;
    border-radius: ${theme.general.borderRadius};
    background: ${theme.colors.alpha.black[100]};
  `
);

const getAttachments = (message) =>
  Array.isArray(message?.attachments)
    ? message.attachments
    : Array.isArray(message?.message_attachments)
      ? message.message_attachments
      : [];

const getAttachmentUrl = (attachment) =>
  attachment?.url || attachment?.public_url || attachment?.publicUrl || attachment?.media_url || '';

const getAttachmentContentType = (attachment) =>
  String(attachment?.contentType || attachment?.content_type || '').toLowerCase();

const getInitials = (name) => String(name || 'Customer')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const MessageContent = ({ item, compact = false }) => {
  const attachments = getAttachments(item);
  const text = String(item?.message || '').trim();

  return (
    <>
      {text && (
        <Typography component="span" variant={compact ? 'body2' : 'body1'}>
          {text}
        </Typography>
      )}
      {attachments.map((attachment, index) => {
        const url = getAttachmentUrl(attachment);
        if (!url) {
          return null;
        }

        const contentType = getAttachmentContentType(attachment);
        const key = attachment.id || `${url}-${index}`;

        if (contentType.startsWith('image/') || attachment.type === 'image') {
          return (
            <Box
              key={key}
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              sx={{ display: 'block' }}
            >
              <MessageImage
                src={url}
                alt={attachment.filename || 'Message attachment'}
                sx={compact ? { maxWidth: 240, maxHeight: 240 } : undefined}
              />
            </Box>
          );
        }

        if (contentType.startsWith('video/') || attachment.type === 'video') {
          return (
            <MessageVideo
              key={key}
              controls
              src={url}
              sx={compact ? { maxWidth: 240, maxHeight: 240 } : undefined}
            />
          );
        }

        return (
          <Typography key={key} component="a" href={url} target="_blank" rel="noreferrer" sx={{ fontWeight: 700 }}>
            {attachment.filename || 'Open attachment'}
          </Typography>
        );
      })}
    </>
  );
};

function ChatContent({
  data,
  customer,
  hasMore,
  loading,
  error,
  loadingMore,
  onLoadMore,
  onRetry,
  compact = false
}) {
  const { user } = useAuth();
  const chatContainerRef = useRef(null);
  const previousLastMessageIdRef = useRef(null);

  useEffect(() => {
    const lastMessage = data[data.length - 1];
    const lastMessageId = lastMessage ? Number(lastMessage.id) : null;

    if (!lastMessageId || loadingMore) {
      previousLastMessageIdRef.current = lastMessageId;
      return;
    }

    if (previousLastMessageIdRef.current !== lastMessageId && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }

    previousLastMessageIdRef.current = lastMessageId;
  }, [data, loadingMore]);

  if (loading) {
    return (
      <Box py={compact ? 4 : 6} display="flex" justifyContent="center">
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box py={compact ? 4 : 6} px={2} textAlign="center">
        <Typography variant={compact ? 'h5' : 'h4'}>No messages yet</Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Select a customer to start messaging.
        </Typography>
        {error && (
          <Typography sx={{ mt: 1 }} variant="subtitle2" color="error.main">
            {error}
          </Typography>
        )}
        {error && (
          <Button sx={{ mt: 1.5 }} size="small" variant="outlined" onClick={onRetry}>
            Retry loading messages
          </Button>
        )}
      </Box>
    );
  }

  let lastDate = null;

  return (
    <ChatContainer ref={chatContainerRef} sx={{ p: compact ? 1.5 : 3 }}>
      {hasMore && (
        <Box mb={2} display="flex" justifyContent="center">
          <Button onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading older messages...' : 'Load older messages'}
          </Button>
        </Box>
      )}

      {data.map((item) => {
        const itemDate = format(new Date(item.createdAt), 'yyyy-MM-dd');
        const isNewDate = itemDate !== lastDate;
        const formattedDate = format(new Date(item.createdAt), 'MMMM dd yyyy');
        lastDate = itemDate;

        return item.type === 'incoming'
          ? (
            <React.Fragment key={item.id}>
              {isNewDate && <DividerWrapper>{formattedDate}</DividerWrapper>}
              <Box display="flex" alignItems="flex-start" justifyContent="flex-start" py={compact ? 1.25 : 2.5}>
                <Avatar
                  variant={compact ? 'circular' : 'rounded'}
                  sx={{ width: compact ? 30 : 42, height: compact ? 30 : 42, fontSize: compact ? 12 : 16 }}
                  alt={item.customer_name || customer?.name}
                  src={customer?.avatar || customer?.photo_url || undefined}
                >
                  {getInitials(item.customer_name || customer?.name)}
                </Avatar>
                <Box ml={compact ? 1 : 2} display="flex" alignItems="flex-start" flexDirection="column">
                  <CardWrapperSecondary
                    sx={{
                      p: compact ? 1.25 : 2,
                      gap: compact ? 0.5 : 1,
                      maxWidth: compact ? 280 : 460
                    }}
                  >
                    <MessageContent item={item} compact={compact} />
                  </CardWrapperSecondary>
                  <Typography variant="subtitle2" sx={{ pt: 0.5, display: 'flex', alignItems: 'center', fontSize: compact ? 10 : undefined }}>
                    {!compact && <ScheduleTwoToneIcon sx={{ mr: 0.5 }} fontSize="small" />}
                    {moment(item.createdAt).fromNow()}
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
            )
          : (
            <React.Fragment key={item.id}>
              {isNewDate && <DividerWrapper>{formattedDate}</DividerWrapper>}
              <Box display="flex" alignItems="flex-start" justifyContent="flex-end" py={compact ? 1.25 : 2.5}>
                <Box mr={compact ? 0 : 2} display="flex" alignItems="flex-end" flexDirection="column">
                  <CardWrapperPrimary
                    sx={{
                      p: compact ? 1.25 : 2,
                      gap: compact ? 0.5 : 1,
                      maxWidth: compact ? 280 : 460
                    }}
                  >
                    <MessageContent item={item} compact={compact} />
                  </CardWrapperPrimary>
                  <Typography variant="subtitle2" sx={{ pt: 0.5, display: 'flex', alignItems: 'center', fontSize: compact ? 10 : undefined }}>
                    {!compact && <ScheduleTwoToneIcon sx={{ mr: 0.5 }} fontSize="small" />}
                    {moment(item.createdAt).fromNow()}
                  </Typography>
                </Box>
                {!compact && (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 42, height: 42 }}
                    alt={user.name}
                    src={user.avatar}
                  />
                )}
              </Box>
            </React.Fragment>
            );
      })}
    </ChatContainer>
  );
}

export default ChatContent;
